if (typeof window.PredictiveSearch === 'undefined') {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.swatchesRedirectUrl = '/collections/free-fabric-swatches';

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    shouldShowPages() {
      // Data attributes come through as strings
      return this.dataset.showPages !== 'false';
    }

    getQuery() {
      return this.input.value.trim();
    }

    getNormalizedQuery() {
      return this.getQuery().toLowerCase();
    }

    redirectIfSwatchesQuery(event) {
      const normalized = this.getNormalizedQuery();
      if (normalized !== 'swatches' && normalized !== 'swatch') return false;
      if (event) event.preventDefault();
      window.location.assign(this.swatchesRedirectUrl);
      return true;
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      // Update the term asap, don't wait for the predictive search query to finish loading
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (this.redirectIfSwatchesQuery(event)) return;
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          if (this.redirectIfSwatchesQuery(event)) break;
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText) {
        // Escape special characters in previousTerm to prevent regex errors
        const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const matches = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g'));
        if (matches && matches.length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
          (element) => element.offsetParent !== null
      );
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) {
        selectedOption.click();
        return;
      }

      // If nothing is selected, fall back to a normal search submit.
      this.input?.form?.submit();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      // Abort any in-flight request for a previous term to avoid race conditions
      this.abortController.abort();
      this.abortController = new AbortController();
      const requestSignal = this.abortController.signal;
      const requestedTerm = searchTerm;

      const extractSectionInnerHtml = (text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        return doc.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
      };

      const buildUrl = (type) => {
        const base = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}`;
        const limit = '&resources[limit]=10';
        const options =
          type === 'product'
            ? '&resources[options][fields]=title,variants.sku,product_type,variants.title,tag'
            : '';
        return `${base}&resources[type]=${type}${limit}${options}&section_id=predictive-search`;
      };

      // This request intentionally omits resources[type] so Shopify/Search & Discovery
      // can apply the store's configured predictive search resource types.
      // We only read Pages from its HTML response.
      const buildConfiguredResourcesUrl = () => {
        const base = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}`;
        return `${base}&resources[limit]=10&section_id=predictive-search`;
      };

      const fetchMarkup = (type) =>
        fetch(buildUrl(type), { signal: requestSignal }).then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        });

      const fetchConfiguredResourcesMarkup = () =>
        fetch(buildConfiguredResourcesUrl(), { signal: requestSignal }).then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        });

      const requests = [fetchMarkup('product')];
      if (this.shouldShowPages()) {
        // Fetch store-configured predictive resources (no resources[type]) so Search & Discovery
        // can apply the store's configured predictive search resource types.
        requests.push(fetchConfiguredResourcesMarkup());
      }

      Promise.all(requests).then(async (responses) => {
          // If the user has typed something else since this request started, ignore this response.
          if (this.searchTerm !== requestedTerm) return;

          const productText = responses[0];
          const configuredResourcesText = responses[1];

          const productMarkup = extractSectionInnerHtml(productText);
          const configuredResourcesMarkup = configuredResourcesText
            ? extractSectionInnerHtml(configuredResourcesText)
            : '';

          // IMPORTANT: Keep product behavior identical by using the product response as the base markup.
          // Only inject Pages from the separate pages request.
          const filteredProductMarkup = await this.filterResultsByTagsAndSynonyms(productMarkup, searchTerm);

          const baseContainer = document.createElement('div');
          baseContainer.innerHTML = filteredProductMarkup;

          const baseDesktopPagesList = baseContainer.querySelector('#predictive-search-results-pages-list-desktop');
          const baseMobilePagesList = baseContainer.querySelector('#predictive-search-results-pages-list-mobile');
          let injectedDesktopCount = 0;
          let injectedMobileCount = 0;

          if (this.shouldShowPages() && configuredResourcesMarkup) {
            const pagesContainer = document.createElement('div');
            pagesContainer.innerHTML = configuredResourcesMarkup;
            const pagesDesktopPagesList = pagesContainer.querySelector('#predictive-search-results-pages-list-desktop');
            const pagesMobilePagesList = pagesContainer.querySelector('#predictive-search-results-pages-list-mobile');

            if (baseDesktopPagesList && pagesDesktopPagesList) {
              baseDesktopPagesList.innerHTML = pagesDesktopPagesList.innerHTML;
            }
            if (baseMobilePagesList && pagesMobilePagesList) {
              baseMobilePagesList.innerHTML = pagesMobilePagesList.innerHTML;
            }

            // If Search & Discovery is configured to show Pages (and there are matches),
            // fetch the full Pages-only response so we can display up to 10 page results.
            const configuredPagesCount = pagesDesktopPagesList ? pagesDesktopPagesList.querySelectorAll('li').length : 0;
            if (configuredPagesCount > 0) {
              const pageOnlyText = await fetchMarkup('page');
              if (this.searchTerm !== requestedTerm) return;
              const pageOnlyMarkup = extractSectionInnerHtml(pageOnlyText);
              const pageOnlyContainer = document.createElement('div');
              pageOnlyContainer.innerHTML = pageOnlyMarkup;
              const fullDesktopPagesList = pageOnlyContainer.querySelector('#predictive-search-results-pages-list-desktop');
              const fullMobilePagesList = pageOnlyContainer.querySelector('#predictive-search-results-pages-list-mobile');
              if (baseDesktopPagesList && fullDesktopPagesList) {
                baseDesktopPagesList.innerHTML = fullDesktopPagesList.innerHTML;
              }
              if (baseMobilePagesList && fullMobilePagesList) {
                baseMobilePagesList.innerHTML = fullMobilePagesList.innerHTML;
              }
            }
          }

          // Show/hide pages wrappers based on injected content and setting
          injectedDesktopCount = baseDesktopPagesList ? baseDesktopPagesList.querySelectorAll('li').length : 0;
          injectedMobileCount = baseMobilePagesList ? baseMobilePagesList.querySelectorAll('li').length : 0;

          const showPagesNow = this.shouldShowPages() && injectedDesktopCount > 0;
          if (showPagesNow) {
            baseDesktopPagesList?.closest('.predictive-search__pages-wrapper')?.removeAttribute('hidden');
            baseMobilePagesList?.closest('.predictive-search__pages-wrapper')?.removeAttribute('hidden');
          } else {
            baseDesktopPagesList?.closest('.predictive-search__pages-wrapper')?.setAttribute('hidden', '');
            baseMobilePagesList?.closest('.predictive-search__pages-wrapper')?.setAttribute('hidden', '');
          }

          const baseWrapper = baseContainer.querySelector('#predictive-search-results-groups-wrapper');
          if (baseWrapper) {
            const hasProducts = baseWrapper.querySelectorAll('#predictive-search-results-products-list li').length > 0;
            const hasPages = showPagesNow;
            if (hasProducts) {
              baseWrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');
            } else {
              baseWrapper.classList.add('predictive-search__results-groups-wrapper--no-products');
            }

            baseWrapper.classList.toggle('predictive-search__results-groups-wrapper--has-products', hasProducts);
            baseWrapper.classList.toggle('predictive-search__results-groups-wrapper--has-pages', hasPages);
            baseWrapper.classList.toggle(
              'predictive-search__results-groups-wrapper--has-both',
              hasProducts && hasPages
            );
          }

          // Update live region text to include injected pages count (simple + stable)
          const liveRegion = baseContainer.querySelector('[data-predictive-search-live-region-count-value]');
          const resultsRoot = baseContainer.querySelector('#predictive-search-results');
          if (liveRegion && resultsRoot) {
            const productCount = resultsRoot.querySelectorAll('#predictive-search-results-products-list li').length;
            const pageCountDesktop = resultsRoot.querySelectorAll('#predictive-search-results-pages-list-desktop li').length;
            const total = productCount + pageCountDesktop;
            liveRegion.textContent = total === 0 ? 'No results' : `${total} results`;
          }

          const mergedHtml = baseContainer.innerHTML;

          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = mergedHtml;
          });
          this.renderSearchResults(mergedHtml);
        }).catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          this.close();
          throw error;
        });
    }

    /**
     * Filters out products that have tags or synonyms containing special characters (_, :)
     * Hyphens (-) are allowed for SKU matching
     * Only alphanumeric tags and synonyms (with hyphens) are considered searchable
     * Uses regex to reject values containing those special characters
     */
    async filterResultsByTagsAndSynonyms(resultsMarkup, searchTerm) {
      const hasNonProductResults = (container) => {
        const hasQueries = container.querySelectorAll('#predictive-search-results-queries-list li').length > 0;
        const hasCollections = container.querySelectorAll('#predictive-search-results-queries-list li').length > 0; // collections share the same list
        const hasPagesDesktop = container.querySelectorAll('#predictive-search-results-pages-list-desktop li').length > 0;
        const hasArticlesDesktop = container.querySelectorAll('#predictive-search-results-pages-list-desktop li').length > 0; // articles share the same list
        const hasPagesMobile = container.querySelectorAll('#predictive-search-results-pages-list-mobile li').length > 0;
        const hasArticlesMobile = container.querySelectorAll('#predictive-search-results-pages-list-mobile li').length > 0; // articles share the same list

        return hasQueries || hasCollections || hasPagesDesktop || hasArticlesDesktop || hasPagesMobile || hasArticlesMobile;
      };

      // Regex pattern to match tags/synonyms containing special characters: _, :
      // Hyphens (-) are excluded from this pattern to allow SKU matching
      const specialCharPattern = /[_:]/;
      // Normalize search term for comparison (lowercase, trim)
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      
      // If search term itself contains special characters (_, :), exclude all products
      // because tags/synonyms with special characters should not be searchable
      if (specialCharPattern.test(searchTerm)) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = resultsMarkup;
        const productListItems = Array.from(tempDiv.querySelectorAll('#predictive-search-results-products-list li'));
        const productsResultGroup = tempDiv.querySelector('.predictive-search__result-group--products');
        const productsList = tempDiv.querySelector('#predictive-search-results-products-list');
        
        if (productListItems.length > 0 || productsList || productsResultGroup) {
          // Create "No results found" message
          const noResultsDiv = document.createElement('div');
          noResultsDiv.className = 'predictive-search__no-results';
          noResultsDiv.innerHTML = `
            <p class="predictive-search__no-results-text">
              No results found for "${searchTerm}". Check the spelling or use a different word or phrase.
            </p>
          `;
          
          // Remove all product items
          if (productListItems.length > 0) {
            productListItems.forEach(item => item.remove());
          }
          
          // Remove the products heading if it exists
          const productsHeading = tempDiv.querySelector('#predictive-search-products');
          if (productsHeading) {
            productsHeading.remove();
          }
          
          const keepNonProductResults = hasNonProductResults(tempDiv);
          // If we still have non-product results (pages, articles, etc.), just remove products.
          // Only show a global "No results" message when there are no results at all.
          if (!keepNonProductResults) {
            const resultsWrapper = tempDiv.querySelector('#predictive-search-results-groups-wrapper');
            const searchResults = tempDiv.querySelector('#predictive-search-results');
            if (productsResultGroup) {
              productsResultGroup.innerHTML = '';
              productsResultGroup.appendChild(noResultsDiv);
            } else if (productsList) {
              const productsContainer = productsList.closest('div');
              if (productsContainer) {
                productsContainer.replaceWith(noResultsDiv);
              } else {
                productsList.replaceWith(noResultsDiv);
              }
            } else if (resultsWrapper) {
              resultsWrapper.appendChild(noResultsDiv);
            } else if (searchResults) {
              searchResults.appendChild(noResultsDiv);
            }

            // Remove "Show All" button only when nothing is left to show
            const searchForButton = tempDiv.querySelector('.predictive-search__search-for-button');
            if (searchForButton) {
              searchForButton.remove();
            }
          } else {
            // Keep the wrapper intact because it can also contain the mobile pages/articles list.
          }
          
        }
        
        return tempDiv.innerHTML;
      }

      // Create a temporary DOM to parse and filter the results
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = resultsMarkup;

      // Find all product list items
      const productListItems = Array.from(tempDiv.querySelectorAll('#predictive-search-results-products-list li'));
      
      // Check if there are no products initially
      if (productListItems.length === 0) {
        // If there are still non-product results (pages, articles, suggestions, etc.), keep them.
        if (hasNonProductResults(tempDiv)) {
          return tempDiv.innerHTML;
        }

        // Check if products section exists but is empty, or doesn't exist at all
        const productsList = tempDiv.querySelector('#predictive-search-results-products-list');
        const productsResultGroup = tempDiv.querySelector('.predictive-search__result-group--products');
        const resultsWrapper = tempDiv.querySelector('#predictive-search-results-groups-wrapper');
        const searchResults = tempDiv.querySelector('#predictive-search-results');
        
        // No products returned from search - add "No results found" message
        const noResultsDiv = document.createElement('div');
        noResultsDiv.className = 'predictive-search__no-results';
        noResultsDiv.innerHTML = `
          <p class="predictive-search__no-results-text">
            No results found for "${searchTerm}". Check the spelling or use a different word or phrase.
          </p>
        `;
        
        if (productsResultGroup) {
          // Products section exists but is empty - replace content with message
          productsResultGroup.innerHTML = '';
          productsResultGroup.appendChild(noResultsDiv);
        } else if (productsList) {
          // Products list exists but is empty - find container and replace
          const productsContainer = productsList.closest('div');
          if (productsContainer) {
            productsContainer.replaceWith(noResultsDiv);
          }
        } else if (resultsWrapper) {
          // Products section doesn't exist - add message to results wrapper
          resultsWrapper.appendChild(noResultsDiv);
        } else if (searchResults) {
          // No wrapper - add directly to search results
          searchResults.appendChild(noResultsDiv);
        }
        
        // Remove the "Show All" button when there are no results
        const searchForButton = tempDiv.querySelector('.predictive-search__search-for-button');
        if (searchForButton) {
          searchForButton.remove();
        }
        
        return tempDiv.innerHTML;
      }

      // Extract product handles and titles from URLs and DOM
      const productData = productListItems.map(item => {
        const link = item.querySelector('a');
        if (!link || !link.href) return null;
        // Extract handle from URL (e.g., /products/handle -> handle)
        const match = link.href.match(/\/products\/([^\/\?]+)/);
        const handle = match ? match[1] : null;
        // Get product title from the heading
        const titleElement = item.querySelector('.predictive-search__item-heading');
        const title = titleElement ? titleElement.textContent.trim() : '';
        return handle ? { handle, title, element: item } : null;
      }).filter(data => data !== null);

      if (productData.length === 0) {
        return resultsMarkup;
      }

      // Fetch product tags for each product using Shopify's product JSON endpoint
      const productTagsPromises = productData.map(async ({ handle, title, element }) => {
        try {
          const response = await fetch(`/products/${handle}.json`, {
            signal: this.abortController.signal,
          });
          if (!response.ok) {
            return { handle, title, element, tags: [], shouldExclude: false };
          }
          const productJson = await response.json();
          const product = productJson.product;
          if (!product) {
            return { handle, title, element, tags: [], shouldExclude: false };
          }
          
          // Handle tags - Shopify returns tags as comma-separated string or array
          let tags = [];
          if (product.tags) {
            if (Array.isArray(product.tags)) {
              tags = product.tags;
            } else if (typeof product.tags === 'string') {
              tags = product.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
            }
          }
          const productType = (product.product_type || '').toLowerCase();
          const variants = product.variants || [];
          const metafields = product.metafields || [];
          
          // Extract synonyms from metafields (check common metafield keys for synonyms)
          const synonyms = [];
          metafields.forEach(metafield => {
            if (metafield.key && metafield.key.toLowerCase().includes('synonym')) {
              const value = metafield.value || '';
              if (typeof value === 'string' && value.trim()) {
                // Handle comma-separated synonyms
                synonyms.push(...value.split(',').map(s => s.trim()).filter(s => s));
              }
            }
          });
          
          // Separate tags/synonyms into clean (alphanumeric only) and invalid (contains special chars)
          const cleanTags = [];
          const invalidTags = [];
          
          [...tags, ...synonyms].forEach(term => {
            if (specialCharPattern.test(term)) {
              invalidTags.push(term);
            } else {
              cleanTags.push(term);
            }
          });
          
          // Check if search term matches any invalid tags/synonyms (with special characters: _ or :)
          const matchingInvalidTerms = invalidTags.filter(term => {
            const normalizedTerm = term.toLowerCase();
            // Check if the search term is contained in the tag/synonym, or if tag contains special chars and matches
            // Also check for exact match or if search term (without special chars) matches tag content
            const searchTermWithoutSpecialChars = normalizedSearchTerm.replace(/[_:]/g, '');
            const tagWithoutSpecialChars = normalizedTerm.replace(/[_:]/g, '');
            
            // Match if: exact match, search term in tag, or tag content matches search term content
            return normalizedTerm === normalizedSearchTerm || 
                   normalizedTerm.includes(normalizedSearchTerm) ||
                   (searchTermWithoutSpecialChars && tagWithoutSpecialChars.includes(searchTermWithoutSpecialChars));
          });
          
          // Check if search term matches any clean tags/synonyms (alphanumeric only)
          const matchingCleanTags = cleanTags.filter(term => {
            const normalizedTerm = term.toLowerCase();
            return normalizedTerm.includes(normalizedSearchTerm);
          });
          
          // Check if product matches on clean searchable fields (title, product_type, SKU, variant title)
          // These are the fields we search that don't involve tags/synonyms
          const matchesOnTitle = title.toLowerCase().includes(normalizedSearchTerm);
          const matchesOnProductType = productType.includes(normalizedSearchTerm);
          const matchesOnSku = variants.some(variant => 
            (variant.sku || '').toLowerCase().includes(normalizedSearchTerm)
          );
          const matchesOnVariantTitle = variants.some(variant => 
            (variant.title || '').toLowerCase().includes(normalizedSearchTerm)
          );
          
          // Product matches on clean fields if it matches any of these
          const matchesOnCleanFields = matchesOnTitle || matchesOnProductType || matchesOnSku || matchesOnVariantTitle;
          
          // Only exclude if product EXCLUSIVELY matches on invalid tags (with special characters: _ or :)
          // Keep product if it matches on ANY clean field (title, SKU, product type, variant title, clean tags)
          // This ensures users see products that match their search term in legitimate fields
          // Hyphens (-) are allowed for SKU matching
          const matchesOnAnyCleanField = matchesOnTitle || matchesOnProductType || matchesOnSku || matchesOnVariantTitle || matchingCleanTags.length > 0;
          const shouldExclude = matchingInvalidTerms.length > 0 && !matchesOnAnyCleanField;
          
          return { handle, title, element, tags, shouldExclude };
        } catch (error) {
          // If fetch fails, keep the product (fail open)
          return { handle, title, element, tags: [], shouldExclude: false };
        }
      });

      // Wait for all product data to be fetched
      const productsWithTags = await Promise.all(productTagsPromises);
      
      // Filter out products that should be excluded
      const validProducts = productsWithTags.filter(({ shouldExclude }) => !shouldExclude);
      
      // If any products were filtered out, rebuild the markup
      if (validProducts.length < productListItems.length) {
        const productsList = tempDiv.querySelector('#predictive-search-results-products-list');
        
        if (productsList) {
          if (validProducts.length === 0) {
            // No products left after filtering - add "No results found" message
            const productsContainer = productsList.closest('div');
            const productsResultGroup = productsList.closest('.predictive-search__result-group--products');
            
            if (productsContainer && productsContainer.querySelector('#predictive-search-products')) {
              // Create a "No results found" message
              const noResultsDiv = document.createElement('div');
              noResultsDiv.className = 'predictive-search__no-results';
              noResultsDiv.innerHTML = `
                <p class="predictive-search__no-results-text">
                  No results found for "${searchTerm}". Check the spelling or use a different word or phrase.
                </p>
              `;
              
              // Replace the container with the message
              productsContainer.replaceWith(noResultsDiv);
            } else if (productsResultGroup) {
              // If container structure is different, add message to result group
              const noResultsDiv = document.createElement('div');
              noResultsDiv.className = 'predictive-search__no-results';
              noResultsDiv.innerHTML = `
                <p class="predictive-search__no-results-text">
                  No results found for "${searchTerm}". Check the spelling or use a different word or phrase.
                </p>
              `;
              productsResultGroup.innerHTML = '';
              productsResultGroup.appendChild(noResultsDiv);
            }
            
            // Remove the "Show All" button when there are no results
            const searchForButton = tempDiv.querySelector('.predictive-search__search-for-button');
            if (searchForButton) {
              searchForButton.remove();
            }
          } else {
            // Some products remain - rebuild the list
            productsList.innerHTML = '';
            validProducts.forEach(({ element }) => {
              productsList.appendChild(element);
            });
          }
        }
        return tempDiv.innerHTML;
      }

      return resultsMarkup;
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      const liveRegionEl = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (liveRegionEl) {
        this.setLiveRegionText(liveRegionEl.textContent);
      }
    }

    getResultsMaxHeight() {
      this.resultsMaxHeight =
          window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }

    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');
    }
  }
  window.PredictiveSearch = PredictiveSearch;

  customElements.define('predictive-search', PredictiveSearch);
}
